import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "layouts/ecommerce/posnavbar/Navbar";
import { UserContext } from "App";
import "./OperationsMenuPage.css";
import { useSnackbar } from "../hooks/SnackbarProvider";
import GeneralConfigDrawer from "./components/GeneralConfigDrawer/GeneralConfigDrawer";

import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CurrencyRupeeOutlinedIcon from "@mui/icons-material/CurrencyRupeeOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import TableRestaurantOutlinedIcon from "@mui/icons-material/TableRestaurantOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import MenuIcon from "@mui/icons-material/Menu";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import KeyboardOutlinedIcon from "@mui/icons-material/KeyboardOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import RepartitionIcon from "@mui/icons-material/Repartition";

const OpsCard = ({ icon, label, onClick, disabled }) => (
  <div
    className="ops-page-card"
    style={disabled ? { cursor: "not-allowed", opacity: 0.5 } : undefined}
    onClick={onClick}
  >
    <div className="ops-page-card-icon">{icon}</div>
    <span className="ops-page-card-label">{label}</span>
  </div>
);

const OperationsMenuPage = () => {
  const navigate = useNavigate();
  const { color, setMenuAvailabilityOpen } = useContext(UserContext);
  const showSnackbar = useSnackbar();
  const retailType = localStorage.getItem("retailType");
  const branchSubType = localStorage.getItem("branchSubType");
  const sessionId = localStorage.getItem("sessionId");
  const appConfig = JSON.parse(localStorage.getItem("appConfig") || "{}");
  const cloudPrintEnabled = appConfig?.all?.CLOUD_PRINT?.flag;
  const enabledDisplayModes = appConfig?.settings?.DISPLAY_SYSTEM_MODE?.kdsDisplayScreens || [];

  const [generalConfigOpen, setGeneralConfigOpen] = useState(false);

  const go = (path) => {
    if (!sessionId) {
      navigate("/session-page/start-day");
      return;
    }
    navigate(path);
  };

  const handleEndDay = () => {
    if (sessionId) {
      showSnackbar("Close session to end day", "error");
    } else {
      navigate("/session-page/day/closing");
    }
  };

  const handleLocation = () => {
    if (sessionId) {
      showSnackbar("Close session to change location", "error");
    } else {
      navigate("/products/location");
    }
  };

  const mainOperations = [
    {
      label: "Home",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-layout-grid w-7 h-7"
          aria-hidden="true"
        >
          <rect width="7" height="7" x="3" y="3" rx="1"></rect>
          <rect width="7" height="7" x="14" y="3" rx="1"></rect>
          <rect width="7" height="7" x="14" y="14" rx="1"></rect>
          <rect width="7" height="7" x="3" y="14" rx="1"></rect>
        </svg>
      ),
      onClick: () => go("/dashboard"),
    },
    {
      label: "Cart",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-shopping-cart w-7 h-7"
          aria-hidden="true"
        >
          <circle cx="8" cy="21" r="1"></circle>
          <circle cx="19" cy="21" r="1"></circle>
          <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"></path>
        </svg>
      ),
      onClick: () => {
        if (branchSubType === "FINE_DINE") {
          go("/products/table-layout");
        } else if (retailType !== "RESTAURANT") {
          go("/grocery/cart");
        } else {
          go("/restaurant/cart");
        }
      },
    },
    {
      label: "Orders",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-globe w-5 h-5 transition-colors text-slate-500 group-hover:text-primary"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"></path>
          <path d="M2 12h20"></path>
        </svg>
      ),
      onClick: () => {
        if (!sessionId) {
          navigate("/session-page/start-day");
          return;
        }
        const userRoles = JSON.parse(localStorage.getItem("user_roles") || "[]");
        const isOdsUser = Array.isArray(userRoles) && userRoles.includes("ODS_USER");
        const orgConfig = JSON.parse(localStorage.getItem("orgConfig") || "{}");
        const showOdsTab = orgConfig?.odsScreen;
        if (retailType === "RESTAURANT") {
          if (!enabledDisplayModes?.find((mode) => mode === "ODS")) {
            navigate("/orders", { state: { initialTab: "Order History" } });
            return;
          }
          if (isOdsUser) {
            navigate("/orders");
          } else {
            navigate("/orders", { state: { initialTab: "Order History" } });
          }
        } else {
          if (!showOdsTab) {
            navigate("/orders", { state: { initialTab: "Order History" } });
            return;
          }
          if (isOdsUser) {
            navigate("/orders");
          } else {
            navigate("/orders", { state: { initialTab: "Order History" } });
          }
        }
      },
    },
    ...(retailType === "RESTAURANT"
      ? [
          {
            label: "Expenses",
            icon: (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-receipt w-7 h-7"
                aria-hidden="true"
              >
                <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"></path>
                <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"></path>
                <path d="M12 17.5v-11"></path>
              </svg>
            ),
            disabled: true,
            onClick: () => {
              // window.dispatchEvent(new CustomEvent("pos:open-expense"));
              showSnackbar("Feature is coming soon", "info");
            },
          },
          {
            label: "Stock Adjustments",
            icon: (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-package w-7 h-7"
                aria-hidden="true"
              >
                <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"></path>
                <path d="M12 22V12"></path>
                <polyline points="3.29 7 12 12 20.71 7"></polyline>
                <path d="m7.5 4.27 9 5.15"></path>
              </svg>
            ),
            onClick: () => go("/products/inventory"),
          },
          {
            label: "Logistics",
            icon: (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-truck w-7 h-7"
                aria-hidden="true"
              >
                <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"></path>
                <path d="M15 18H9"></path>
                <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"></path>
                <circle cx="17" cy="18" r="2"></circle>
                <circle cx="7" cy="18" r="2"></circle>
              </svg>
            ),
            onClick: () => {
              window.dispatchEvent(new CustomEvent("pos:open-logistics"));
            },
          },
          {
            label: "Item On/Off",
            icon: (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-toggle-left w-7 h-7"
                aria-hidden="true"
              >
                <circle cx="9" cy="12" r="3"></circle>
                <rect width="20" height="14" x="2" y="5" rx="7"></rect>
              </svg>
            ),
            onClick: () => setMenuAvailabilityOpen(true),
          },
        ]
      : [
          {
            label: "Expense",
            icon: (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-receipt w-7 h-7"
                aria-hidden="true"
              >
                <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"></path>
                <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"></path>
                <path d="M12 17.5v-11"></path>
              </svg>
            ),
            disabled: true,
            onClick: () => {
              // window.dispatchEvent(new CustomEvent("pos:open-expense"));
              // go("/products/expenses")
              showSnackbar("Feature is coming soon", "info");
            },
          },
          {
            label: "Logistics",
            icon: (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-truck w-7 h-7"
                aria-hidden="true"
              >
                <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"></path>
                <path d="M15 18H9"></path>
                <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"></path>
                <circle cx="17" cy="18" r="2"></circle>
                <circle cx="7" cy="18" r="2"></circle>
              </svg>
            ),
            onClick: () => {
              // go("/logistics")
              window.dispatchEvent(new CustomEvent("pos:open-logistics"));
            },
          },
          ...(retailType !== "GROCERY"
            ? [
                {
                  label: "Menu",
                  icon: <MenuIcon sx={{ fontSize: "1.3rem" }} />,
                  onClick: () => go("/settings/create-menu"),
                },
              ]
            : []),
        ]),

    {
      label: "Hold Orders",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-circle-pause w-7 h-7"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="10" x2="10" y1="15" y2="9"></line>
          <line x1="14" x2="14" y1="15" y2="9"></line>
        </svg>
      ),
      onClick: () => {
        if (!sessionId) {
          navigate("/session-page/start-day");
          return;
        }
        window.dispatchEvent(new CustomEvent("pos:open-hold-orders"));
      },
    },
    {
      label: "Customers",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-heart w-7 h-7"
          aria-hidden="true"
        >
          <path d="M2 9.5a5.5 5.5 0 0 1 9.591-3.676.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 2.29-1.5 4-3 5.5l-5.492 5.313a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5"></path>
        </svg>
      ),
      onClick: () => go("/products/customertable"),
    },
    ...(retailType !== "GROCERY"
      ? [
          {
            label: "Menu",
            icon: (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-file-text w-7 h-7"
                aria-hidden="true"
              >
                <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                <path d="M10 9H8" />
                <path d="M16 13H8" />
                <path d="M16 17H8" />
              </svg>
            ),
            onClick: () => {
              if (!sessionId) {
                navigate("/session-page/start-day");
                return;
              }
              navigate("/settings/create-menu");
            },
          },
        ]
      : []),
    ...(retailType === "RESTAURANT" && branchSubType === "FINE_DINE"
      ? [
          {
            label: "Tables",
            icon: (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-calendar w-7 h-7"
                aria-hidden="true"
              >
                <path d="M8 2v4" />
                <path d="M16 2v4" />
                <rect width="18" height="18" x="3" y="4" rx="2" />
                <path d="M3 10h18" />
              </svg>
            ),
            onClick: () => {
              if (!sessionId) {
                navigate("/session-page/start-day");
                return;
              }
              navigate("/products/table-layout");
            },
          },
        ]
      : []),
    {
      label: "Keyboard Shortcuts",
      icon: <KeyboardOutlinedIcon sx={{ fontSize: "1.3rem" }} />,
      onClick: () => {
        if (!sessionId) {
          navigate("/session-page/start-day");
          return;
        }
        window.dispatchEvent(new CustomEvent("pos:open-shortcuts"));
      },
    },

    // {
    //   label: "End Day",
    //   icon: <EventBusyOutlinedIcon sx={{ fontSize: "1.3rem" }} />,
    //   onClick: handleEndDay,
    // },
  ];

  const settingsOperations = [
    {
      label: "Peripherals",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-sliders-vertical h-7 w-7"
          aria-hidden="true"
        >
          <path d="M10 8h4"></path>
          <path d="M12 21v-9"></path>
          <path d="M12 8V3"></path>
          <path d="M17 16h4"></path>
          <path d="M19 12V3"></path>
          <path d="M19 21v-5"></path>
          <path d="M3 14h4"></path>
          <path d="M5 10V3"></path>
          <path d="M5 21v-7"></path>
        </svg>
      ),
      onClick: () => go("/settings/peripherals"),
    },
    // ...(retailType === "RESTAURANT"
    //   ? [
    //       {
    //         label: "Bill/ KOT templates",
    //         icon: <DescriptionOutlinedIcon sx={{ fontSize: "1.3rem" }} />,
    //         onClick: () => {
    //           if (!sessionId) {
    //             navigate("/session-page/start-day");
    //             return;
    //           }
    //           showSnackbar("Feature coming soon", "info");
    //         },
    //       },
    //     ]
    //   : []),
    {
      label: "Printer configuration",
      icon: <PrintOutlinedIcon sx={{ fontSize: "1.3rem" }} />,
      onClick: () => go("/settings/printer-configuration"),
    },
    {
      label: "Dual Screen",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-monitor h-7 w-7"
          aria-hidden="true"
        >
          <rect width="20" height="14" x="2" y="3" rx="2" />
          <line x1="8" x2="16" y1="21" y2="21" />
          <line x1="12" x2="12" y1="17" y2="21" />
        </svg>
      ),
      onClick: () => {
        const existing = window.open("", "pallet-dual-screen");
        if (existing && existing.location && existing.location.pathname === "/dual-screen") {
          existing.focus();
        } else {
          window.open(
            "/dual-screen",
            "pallet-dual-screen",
            "width=1280,height=800,menubar=no,toolbar=no,location=no,scrollbars=no,status=no"
          );
        }
      },
    },
    // {
    //   label: "Discounts",
    //   icon: <LocalOfferOutlinedIcon sx={{ fontSize: "1.3rem" }} />,
    //   // onClick: () => go("/settings"),
    //   onClick: () => showSnackbar("Feature comming soon", "info"),
    // },
    {
      label: "Performance",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-chart-no-axes-column-increasing w-7 h-7"
          aria-hidden="true"
        >
          <path d="M5 21v-6"></path>
          <path d="M12 21V9"></path>
          <path d="M19 21V3"></path>
        </svg>
      ),
      onClick: () => go("/settings/trouble-shoot"),
    },
    {
      label: "Help & Support",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-circle-question-mark w-7 h-7"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
          <path d="M12 17h.01"></path>
        </svg>
      ),
      onClick: () => {
        if (!sessionId) {
          navigate("/session-page/start-day");
          return;
        }
        window.dispatchEvent(new CustomEvent("pos:open-help-and-support"));
      },
    },
    {
      label: "Change theme",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-palette w-7 h-7"
          aria-hidden="true"
        >
          <path d="M12 22a1 1 0 0 1 0-20 10 9 0 0 1 10 9 5 5 0 0 1-5 5h-2.25a1.75 1.75 0 0 0-1.4 2.8l.3.4a1.75 1.75 0 0 1-1.4 2.8z"></path>
          <circle cx="13.5" cy="6.5" r=".5" fill="currentColor"></circle>
          <circle cx="17.5" cy="10.5" r=".5" fill="currentColor"></circle>
          <circle cx="6.5" cy="12.5" r=".5" fill="currentColor"></circle>
          <circle cx="8.5" cy="7.5" r=".5" fill="currentColor"></circle>
        </svg>
      ),
      onClick: () => go("/settings/themes"),
    },
    {
      label: "General configurations",
      icon: <CreditCardOutlinedIcon sx={{ fontSize: "1.3rem" }} />,
      onClick: () => {
        if (!sessionId) {
          navigate("/session-page/start-day");
          return;
        }
        setGeneralConfigOpen(true);
      },
    },
    ...(branchSubType === "FINE_DINE" && cloudPrintEnabled
      ? [
          {
            label: "Printer Agent",
            icon: <SupportAgentIcon sx={{ fontSize: "1.3rem" }} />,
            onClick: () => go("/settings/printer-agent"),
          },
        ]
      : []),
    {
      label: "Account & Subscription",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
          <path d="M14 8H8M16 12H8M12 16H8" />
        </svg>
      ),
      onClick: () => navigate("/settings/account-subscription"),
    },
    {
      label: "Apps & Integrations",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="2" y="2" width="9" height="9" rx="2" />
          <rect x="13" y="2" width="9" height="9" rx="2" />
          <rect x="2" y="13" width="9" height="9" rx="2" />
          <path d="M17.5 13v9M13 17.5h9" />
        </svg>
      ),
      onClick: () => navigate("/settings/apps-integrations"),
    },
    {
      label: "Staff Management",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
      onClick: () => navigate("/products/staff-management"),
    },
  ];

  return (
    <Navbar showTopNavbar={false} showBottomNavbar={false}>
      <GeneralConfigDrawer open={generalConfigOpen} onClose={() => setGeneralConfigOpen(false)} />
      <div className={`ops-page-wrapper ${color === "dark" ? "dark" : ""}`}>
        <section className="ops-page-top-navbar-section">
          <header className="ops-page-navbar">
            <div className="ops-page-navbar-brand">
              <div className="ops-page-navbar-logo">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect width="7" height="7" x="3" y="3" rx="1" />
                  <rect width="7" height="7" x="14" y="3" rx="1" />
                  <rect width="7" height="7" x="14" y="14" rx="1" />
                  <rect width="7" height="7" x="3" y="14" rx="1" />
                </svg>
              </div>
              <div className="ops-page-navbar-titles">
                <h1 className="ops-page-navbar-app-name">Pallet Lynx</h1>
                <span className="ops-page-navbar-subtitle">Navigation</span>
              </div>
            </div>

            <button
              className="ops-page-navbar-close-btn"
              aria-label="Close navigation"
              onClick={() => navigate("/dashboard")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </header>
        </section>

        <p className="ops-page-section-title">Main Operations</p>
        <div className="ops-page-grid">
          {mainOperations.map((item) => (
            <OpsCard
              key={item.label}
              icon={item.icon}
              label={item.label}
              onClick={item.onClick}
              disabled={item.disabled}
            />
          ))}
        </div>

        <p className="ops-page-section-title">Settings &amp; Configuration</p>
        <div className="ops-page-grid">
          {settingsOperations.map((item) => (
            <OpsCard
              key={item.label}
              icon={item.icon}
              label={item.label}
              onClick={item.onClick}
              disabled={item.disabled}
            />
          ))}
        </div>
      </div>
    </Navbar>
  );
};

export default OperationsMenuPage;
